import { useMutation, UseMutationResult, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { ADMIN_API_URL_SUBPATH } from '@/constants';
import { axiosInstance, CreateEntityResponse, INGREDIENT } from '@/core';
import { Ingredient, IngredientForm, IngredientResponse } from '@/features';

export const useIngredients = (config?: {
  onSuccess?: (data: AxiosResponse<CreateEntityResponse>) => void;
  onError?: (error: unknown) => void;
}) => {
  const queryConfig = {
    url: `${ADMIN_API_URL_SUBPATH}/${INGREDIENT}`,
  };

  const createMutation: UseMutationResult<
    AxiosResponse<CreateEntityResponse>,
    unknown,
    IngredientForm,
    unknown
  > = useMutation({
    mutationKey: ['createIngredient'],
    mutationFn: (data: IngredientForm) => axiosInstance.post(`${queryConfig.url}`, data),
    onError: (error) => {
      config?.onError?.(error);
    },
    onSuccess: (data) => {
      config?.onSuccess?.(data);
    },
  });

  const useIngredientsQuery = ({ limit, page }: { limit: number; page: number }) =>
    useQuery<IngredientResponse>({
      queryKey: ['ingredients', limit, page],
      queryFn: async () => {
        const response = await axiosInstance.get<IngredientResponse>(`${queryConfig.url}`, {
          params: { limit, page },
        });
        return response.data;
      },
    });

  const useSearchIngredientByName = ({ name }: { name: string }) =>
    useQuery<Ingredient[]>({
      queryKey: ['ingredients', name],
      queryFn: async () => {
        if (name === '') return [];
        const response = await axiosInstance.get<Ingredient[]>(`${queryConfig.url}/search`, {
          params: { name },
        });
        return response.data;
      },
    });

  const useGetIngredientById = (id: number | undefined) =>
    useQuery<Ingredient>({
      queryKey: ['ingredients', id],
      enabled: !!id,
      queryFn: async () => {
        const response = await axiosInstance.get<Ingredient>(`${queryConfig.url}/${id}`);
        return response.data;
      },
    });

  const duplicateMutation = useMutation({
    mutationKey: ['duplicateIngredient'],
    mutationFn: (id: number) => axiosInstance.post(`${queryConfig.url}/${id}/duplicate`),
  });

  const updateMutation = useMutation({
    mutationKey: ['updateIngredient'],
    mutationFn: (data: Ingredient) => axiosInstance.put(`${queryConfig.url}/${data.id}`, data),
  });

  const deleteMutation = useMutation({
    mutationKey: ['deleteIngredient'],
    mutationFn: (id: number) => axiosInstance.delete(`${queryConfig.url}/${id}`),
  });

  const activateMutation = useMutation({
    mutationKey: ['activateIngredient'],
    mutationFn: (id: number) => axiosInstance.put(`${queryConfig.url}/${id}`, { is_active: true }),
  });

  const deactivateMutation = useMutation({
    mutationKey: ['deactivateIngredient'],
    mutationFn: (id: number) => axiosInstance.put(`${queryConfig.url}/${id}`, { is_active: false }),
  });

  return {
    mutations: {
      createMutation,
      updateMutation,
      duplicateMutation,
      deleteMutation,
      activateMutation,
      deactivateMutation,
    },
    queries: {
      useIngredientsQuery,
      useGetIngredientById,
      useSearchIngredientByName,
    },
  };
};
